import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import {
  COUNTRY_DIAL_CODES_ORDERED,
  DEFAULT_COUNTRY_ISO2,
  countryFlagUrl,
  getCountryByIso2,
} from "../../modal/auth/countryCodes";
import type { FloatingInputInlineEndVariant } from "./FloatingInput";

interface PhoneInputInlineEndButton {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant: FloatingInputInlineEndVariant;
}

interface PhoneInputWithCountryProps {
  phoneNumber: string;
  onPhoneNumberChange: (value: string) => void;
  countryIso2: string;
  onCountryChange: (iso2: string) => void;
  label?: string;
  placeholder?: string;
  name?: string;
  id?: string;
  rootClassName?: string;
  inlineEndButton?: PhoneInputInlineEndButton;
  autoComplete?: string;
  disabled?: boolean;
}

function CountryFlagImg({
  iso2,
  name,
}: {
  iso2: string;
  name: string;
}) {
  return (
    <img
      className="phoneCountryPicker__flagImg"
      src={countryFlagUrl(iso2, 40)}
      srcSet={`${countryFlagUrl(iso2, 80)} 2x`}
      width={28}
      height={21}
      alt=""
      title={name}
      loading="lazy"
      draggable={false}
    />
  );
}

const PhoneInputWithCountry: React.FC<PhoneInputWithCountryProps> = ({
  phoneNumber,
  onPhoneNumberChange,
  countryIso2,
  onCountryChange,
  label = "Phone number",
  placeholder = "Phone number",
  name = "phoneNumber",
  id,
  rootClassName,
  inlineEndButton,
  autoComplete = "tel-national",
  disabled = false,
}) => {
  const inputId = id || name;
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");

  const selectedCountry =
    getCountryByIso2(countryIso2) ||
    getCountryByIso2(DEFAULT_COUNTRY_ISO2)!;

  const filteredCountries = useMemo(() => {
    const query = countrySearch.trim().toLowerCase();
    if (!query) return COUNTRY_DIAL_CODES_ORDERED;

    const queryDigits = query.replace(/\D/g, "");
    return COUNTRY_DIAL_CODES_ORDERED.filter((country) => {
      const nameMatch = country.name.toLowerCase().includes(query);
      const isoMatch = country.iso2.toLowerCase().includes(query);
      const dialMatch =
        country.dialCode.includes(queryDigits) ||
        `+${country.dialCode}`.includes(query);
      return nameMatch || isoMatch || (queryDigits ? dialMatch : false);
    });
  }, [countrySearch]);

  const closeDropdown = () => {
    setIsOpen(false);
    setCountrySearch("");
  };

  useEffect(() => {
    if (!isOpen) {
      setCountrySearch("");
      return;
    }

    let removeListeners: (() => void) | undefined;
    const timer = window.setTimeout(() => {
      searchRef.current?.focus();

      const handlePointerDown = (event: Event) => {
        if (!containerRef.current?.contains(event.target as Node)) {
          closeDropdown();
        }
      };
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") closeDropdown();
      };

      document.addEventListener("pointerdown", handlePointerDown);
      document.addEventListener("keydown", handleKeyDown);
      removeListeners = () => {
        document.removeEventListener("pointerdown", handlePointerDown);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, 0);

    return () => {
      window.clearTimeout(timer);
      removeListeners?.();
    };
  }, [isOpen]);

  const rootClass = [
    "floatingInput",
    "floatingInput--inlineEnd",
    "floatingInput--phoneCountry",
    isOpen ? "is-open" : "",
    rootClassName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass} ref={containerRef}>
      <div className="floatingInput__row">
        <div className="phoneCountryPicker">
          <button
            type="button"
            className="phoneCountryPicker__trigger"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            aria-label={`Country, ${selectedCountry.name} +${selectedCountry.dialCode}`}
            disabled={disabled}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (disabled) return;
              setIsOpen((open) => {
                if (open) setCountrySearch("");
                return !open;
              });
            }}
          >
            <CountryFlagImg
              iso2={selectedCountry.iso2}
              name={selectedCountry.name}
            />
            <span className="phoneCountryPicker__dial">
              +{selectedCountry.dialCode}
            </span>
            <span className="phoneCountryPicker__caret" aria-hidden>
              ▾
            </span>
          </button>
        </div>

        <Form.Control
          id={inputId}
          name={name}
          value={phoneNumber}
          autoComplete={autoComplete}
          disabled={disabled}
          inputMode="tel"
          aria-label={label}
          onChange={(e) => onPhoneNumberChange(e.target.value)}
          className="form-control"
          type="tel"
          placeholder={placeholder}
        />

        {inlineEndButton && (
          <button
            type="button"
            className={`floatingInput__endBtn floatingInput__endBtn--${inlineEndButton.variant}`}
            onClick={inlineEndButton.onClick}
            disabled={inlineEndButton.disabled}
          >
            {inlineEndButton.label}
          </button>
        )}
      </div>

      {isOpen && (
        <div className="phoneCountryPicker__dropdown" role="presentation">
          <div className="phoneCountryPicker__search">
            <input
              ref={searchRef}
              type="text"
              className="phoneCountryPicker__searchInput"
              placeholder="Search"
              value={countrySearch}
              onChange={(e) => setCountrySearch(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              aria-label="Search dial code"
              autoComplete="off"
            />
          </div>
          <ul
            id={listboxId}
            className="phoneCountryPicker__list"
            role="listbox"
            aria-label="Countries"
          >
            {filteredCountries.length === 0 ? (
              <li className="phoneCountryPicker__empty">No matches</li>
            ) : (
              filteredCountries.map((country) => {
                const selected = country.iso2 === selectedCountry.iso2;
                return (
                  <li key={country.iso2} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      aria-label={`${country.name} +${country.dialCode}`}
                      title={`${country.name} (+${country.dialCode})`}
                      className={`phoneCountryPicker__option${
                        selected ? " is-selected" : ""
                      }`}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onCountryChange(country.iso2);
                        closeDropdown();
                      }}
                    >
                      <CountryFlagImg iso2={country.iso2} name={country.name} />
                      <span className="phoneCountryPicker__dial">
                        +{country.dialCode}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}

      <label htmlFor={inputId}>{label}</label>
    </div>
  );
};

export default PhoneInputWithCountry;
